import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, RefreshCw, Settings, DollarSign, TrendingUp, Calendar, HardDrive, MessageSquare, Sparkles, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Area, AreaChart } from "recharts";
import { formatCurrency } from "@/lib/currency";

/**
 * Full-stack tech-costs dashboard. Gated by `costs:read` (view) and
 * `costs:manage` (edit rates + fixed monthly items). Master admins
 * bypass all permission checks.
 *
 * Shows spend across every source we can measure or estimate:
 *   • LLM (metered from llm_usage collection)
 *   • R2 storage (live boto3 sum × configured $/GB-month)
 *   • WhatsApp (count × configured $/message)
 *   • Fixed monthly (Emergent, Clerk, MongoDB, Namecheap — editable)
 *
 * KPI strip: Since inception · This month · Today · Projected end-of-month
 * Below that: date-range picker, daily-total chart, per-service table,
 * and a settings dialog for the editable knobs (gated by costs:manage).
 */
export default function CostsPage() {
  const { hasPerm, isMaster } = useAuth();
  const canManage = isMaster || hasPerm?.("costs:manage");
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(iso(monthStart));
  const [toDate, setToDate] = useState(iso(today));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate + "T23:59:59";
      const res = await api.get("/costs/summary", { params });
      setSummary(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cost summary");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "mtd") {
      setFromDate(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setToDate(iso(now));
    } else if (preset === "last30") {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      setFromDate(iso(d));
      setToDate(iso(now));
    } else if (preset === "ytd") {
      setFromDate(iso(new Date(now.getFullYear(), 0, 1)));
      setToDate(iso(now));
    } else if (preset === "inception") {
      setFromDate(summary?.since_inception?.inception?.slice(0, 10) || "2025-01-01");
      setToDate(iso(now));
    }
  };

  const todayCost = useMemo(() => {
    if (!summary?.series?.length) return 0;
    const last = summary.series[summary.series.length - 1];
    return last?.total_usd || 0;
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="costs-page">
      <PageHeader
        title="Tech costs"
        subtitle="Live spend across every service the app touches. All figures are USD."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="costs-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
            {canManage && (
              <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="costs-settings">
                <Settings className="h-4 w-4 mr-2" /> Rates & fixed costs
              </Button>
            )}
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Since inception"
          value={summary?.since_inception?.total_usd}
          sub={summary?.since_inception?.inception ? `from ${new Date(summary.since_inception.inception).toLocaleDateString()}` : "loading…"}
          icon={Calendar}
          testId="costs-kpi-lifetime"
        />
        <KpiCard
          label="This month"
          value={summary?.month_to_date?.total_usd}
          sub={summary ? `day ${Math.floor(summary.month_to_date.elapsed_days)}/${summary.month_to_date.days_in_month}` : "loading…"}
          icon={TrendingUp}
          testId="costs-kpi-mtd"
        />
        <KpiCard
          label="Today"
          value={todayCost}
          sub="LLM + WhatsApp (variable only)"
          icon={Sparkles}
          testId="costs-kpi-today"
        />
        <KpiCard
          label="Projected this month"
          value={summary?.projected_month_total_usd}
          sub="based on current burn + fixed costs"
          icon={DollarSign}
          tone="brass"
          testId="costs-kpi-projected"
        />
      </div>

      {/* Date range picker */}
      <Card className="p-4 md:p-5" data-testid="costs-range-card">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-[160px]" data-testid="costs-from-input" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-[160px]" data-testid="costs-to-input" />
          </div>
          <Button onClick={load} disabled={loading} data-testid="costs-apply-range">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Apply
          </Button>
          <div className="flex gap-1 ml-2">
            <Button size="sm" variant="ghost" onClick={() => applyPreset("mtd")}>MTD</Button>
            <Button size="sm" variant="ghost" onClick={() => applyPreset("last30")}>Last 30d</Button>
            <Button size="sm" variant="ghost" onClick={() => applyPreset("ytd")}>YTD</Button>
            <Button size="sm" variant="ghost" onClick={() => applyPreset("inception")}>All time</Button>
          </div>
          {summary && (
            <div className="ml-auto text-sm text-muted-foreground">
              Range total: <span className="font-semibold text-foreground font-mono" data-testid="costs-range-total">{formatCurrency(summary.range.total_usd, "USD")}</span> across {summary.range.days} days
            </div>
          )}
        </div>
      </Card>

      {/* Trend chart */}
      <Card className="p-4 md:p-5" data-testid="costs-chart-card">
        <div className="text-sm font-medium mb-3">Daily spend (variable services only)</div>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={summary?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                formatter={(v) => `$${Number(v).toFixed(4)}`}
                contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="llm_usd" name="LLM" stackId="1" stroke="hsl(var(--ap-forest))" fill="hsl(var(--ap-forest) / 0.2)" />
              <Area type="monotone" dataKey="whatsapp_usd" name="WhatsApp" stackId="1" stroke="hsl(var(--ap-brass))" fill="hsl(var(--ap-brass) / 0.2)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          R2 storage and fixed monthly costs are steady-state and excluded from this chart to keep the variable trend readable. See the breakdown table below for the full picture.
        </p>
      </Card>

      {/* Breakdown table */}
      <Card className="p-4 md:p-5 overflow-x-auto" data-testid="costs-breakdown-card">
        <div className="text-sm font-medium mb-3">Range breakdown by service</div>
        {summary ? (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2">Service</th>
                <th className="text-right py-2">Volume</th>
                <th className="text-right py-2">Unit rate</th>
                <th className="text-right py-2">USD in range</th>
              </tr>
            </thead>
            <tbody>
              <BreakdownRow
                icon={Sparkles}
                name="LLM (OpenAI GPT-4o-mini via Emergent)"
                volume={`${summary.range.by_service.llm.calls.toLocaleString()} calls`}
                rate={`× ${summary.range.by_service.llm.markup} markup`}
                usd={summary.range.by_service.llm.usd}
              />
              <BreakdownRow
                icon={MessageSquare}
                name="WhatsApp Business API"
                volume={`${summary.range.by_service.whatsapp.messages.toLocaleString()} msgs`}
                rate={`$${summary.range.by_service.whatsapp.per_message_usd}/msg`}
                usd={summary.range.by_service.whatsapp.usd}
              />
              <BreakdownRow
                icon={HardDrive}
                name="Cloudflare R2 storage"
                volume={`${summary.range.by_service.r2_storage.gb} GB · ${summary.range.by_service.r2_storage.objects.toLocaleString()} objects`}
                rate={`$${summary.range.by_service.r2_storage.usd_month}/mo`}
                usd={summary.range.by_service.r2_storage.usd_for_range}
              />
              {summary.range.by_service.fixed.items.map((item) => (
                <BreakdownRow
                  key={item.id}
                  name={item.name}
                  volume={item.category || "Fixed"}
                  rate={item.monthly_usd > 0 ? `$${item.monthly_usd}/mo` : <span className="text-muted-foreground">not set</span>}
                  usd={item.usd_for_range}
                />
              ))}
              <tr className="border-t-2 border-border">
                <td className="py-2 font-semibold">Total</td>
                <td colSpan={2}></td>
                <td className="py-2 text-right font-mono font-semibold" data-testid="costs-table-total">
                  {formatCurrency(summary.range.total_usd, "USD")}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading breakdown…</div>
        )}
      </Card>

      <CostSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={load} />
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone = "default", testId }) {
  const toneCls = tone === "brass"
    ? "border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.06)]"
    : "border-border";
  return (
    <Card className={`p-4 ${toneCls}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
      </div>
      <div className="mt-2 font-serif text-2xl tabular-nums" data-testid={`${testId}-value`}>
        {typeof value === "number" ? formatCurrency(value, "USD") : <span className="text-muted-foreground text-base">—</span>}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1 truncate" title={sub}>{sub}</div>}
    </Card>
  );
}

function BreakdownRow({ icon: Icon, name, volume, rate, usd }) {
  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/20">
      <td className="py-2 flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />}
        <span>{name}</span>
      </td>
      <td className="py-2 text-right text-xs text-muted-foreground">{volume}</td>
      <td className="py-2 text-right text-xs text-muted-foreground">{rate}</td>
      <td className="py-2 text-right font-mono tabular-nums">
        {typeof usd === "number" ? formatCurrency(usd, "USD") : usd}
      </td>
    </tr>
  );
}

/**
 * Settings dialog — lets holders of `costs:manage` (and master admins)
 * tweak per-unit rates and add / edit / remove fixed monthly cost
 * line items. Changes go through PUT /api/costs/config which
 * validates + audits.
 */
function CostSettingsDialog({ open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/costs/config");
        if (!cancelled) setCfg(res.data);
      } catch (_e) {
        toast.error("Could not load cost config");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const addRow = () => {
    setCfg((c) => ({
      ...c,
      fixed_monthly: [
        ...(c.fixed_monthly || []),
        { id: `custom_${Date.now()}`, name: "New service", category: "Other", monthly_usd: 0, notes: "" },
      ],
    }));
  };
  const removeRow = (id) => {
    setCfg((c) => ({ ...c, fixed_monthly: (c.fixed_monthly || []).filter((r) => r.id !== id) }));
  };
  const patchRow = (id, patch) => {
    setCfg((c) => ({
      ...c,
      fixed_monthly: (c.fixed_monthly || []).map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  };
  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.put("/costs/config", {
        llm_markup: Number(cfg.llm_markup),
        r2_storage_usd_per_gb_month: Number(cfg.r2_storage_usd_per_gb_month),
        whatsapp_usd_per_message: Number(cfg.whatsapp_usd_per_message),
        fixed_monthly: (cfg.fixed_monthly || []).map((r) => ({
          id: r.id,
          name: r.name,
          category: r.category || null,
          monthly_usd: Number(r.monthly_usd) || 0,
          notes: r.notes || "",
        })),
      });
      toast.success("Cost settings saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="costs-settings-dialog">
        <DialogHeader>
          <DialogTitle>Rates & fixed monthly costs</DialogTitle>
        </DialogHeader>
        {!cfg ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Variable-rate knobs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">LLM markup multiplier</Label>
                <Input
                  type="number" step="0.05" min="0.5" max="5.0"
                  value={cfg.llm_markup}
                  onChange={(e) => setCfg({ ...cfg, llm_markup: e.target.value })}
                  data-testid="costs-config-llm-markup"
                />
                <p className="text-[10px] text-muted-foreground mt-1">1.0 = OpenAI list price. Bump if Emergent adds margin.</p>
              </div>
              <div>
                <Label className="text-xs">R2 storage $/GB-month</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={cfg.r2_storage_usd_per_gb_month}
                  onChange={(e) => setCfg({ ...cfg, r2_storage_usd_per_gb_month: e.target.value })}
                  data-testid="costs-config-r2-rate"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Cloudflare list: $0.015 (2026-07).</p>
              </div>
              <div>
                <Label className="text-xs">WhatsApp $/message (blended)</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={cfg.whatsapp_usd_per_message}
                  onChange={(e) => setCfg({ ...cfg, whatsapp_usd_per_message: e.target.value })}
                  data-testid="costs-config-whatsapp-rate"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Meta bills per conversation category; use your blended average.</p>
              </div>
            </div>

            {/* Fixed monthly line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Fixed monthly line items</div>
                <Button size="sm" variant="ghost" onClick={addRow} data-testid="costs-config-add-row">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-[0.14em] text-muted-foreground bg-muted/40">
                    <tr>
                      <th className="text-left py-2 px-3">Service</th>
                      <th className="text-left py-2 px-3">Category</th>
                      <th className="text-right py-2 px-3 w-[120px]">$ / month</th>
                      <th className="text-left py-2 px-3 hidden md:table-cell">Notes</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cfg.fixed_monthly || []).map((row) => (
                      <tr key={row.id} className="border-t border-border">
                        <td className="p-2">
                          <Input
                            value={row.name}
                            onChange={(e) => patchRow(row.id, { name: e.target.value })}
                            className="h-8 text-xs"
                            data-testid={`costs-config-row-name-${row.id}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={row.category || ""}
                            onChange={(e) => patchRow(row.id, { category: e.target.value })}
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number" step="0.01" min="0"
                            value={row.monthly_usd}
                            onChange={(e) => patchRow(row.id, { monthly_usd: e.target.value })}
                            className="h-8 text-xs text-right"
                            data-testid={`costs-config-row-usd-${row.id}`}
                          />
                        </td>
                        <td className="p-2 hidden md:table-cell">
                          <Input
                            value={row.notes || ""}
                            onChange={(e) => patchRow(row.id, { notes: e.target.value })}
                            className="h-8 text-xs"
                            placeholder="Optional context"
                          />
                        </td>
                        <td className="p-2">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => removeRow(row.id)}
                            className="h-7 w-7"
                            data-testid={`costs-config-row-delete-${row.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !cfg} data-testid="costs-config-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
